import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { blockedUserCount, tenantsListPath, tenantsOf, TIER_UNSET, type McpTenantList, type McpTenantSummary } from "../../api/mcp";
import { CouldNotLook, Denied, LoadFailed, Loading, SignedOut } from "../../components/states";
import { Badge, Count, Empty, FilterBar, Notice, Page, Panel, SearchField, SelectField, Table, Td, Th } from "../../components/ui";
import type { AppConfig } from "../../config";
import { Button, Input } from "../../f7";
import { useResource } from "../../hooks/useResource";
import { NotWired, ProductScope, StoredAs } from "../../products/PerProduct";

export function Tenants({ config }: { config: AppConfig }) {
  const [q, setQ] = useState("");
  const [lookup, setLookup] = useState("");
  // "" = every tier, TIER_UNSET = merchants with no entitlements row.
  // Named tiers go on the wire as ?tier=. TIER_UNSET cannot: the server matches
  // the stored string exactly, and a tier-less merchant stores null.
  const [tier, setTier] = useState("");
  const { state, reload } = useResource<McpTenantList>(config, tenantsListPath(tier), [tier]);
  const navigate = useNavigate();

  return (
    <Page
      title="All tenants"
      subtitle="Every merchant this console can see, and what each product stores for them."
    >
      {/* WHOSE LIST THIS IS. It comes from GET /mcp/admin/tenants, so it holds
          merchants that have used MCP. A merchant using Labeeb and not MCP is
          absent from this table entirely — not shown as inactive, ABSENT — and
          that is invisible without saying it. The per-row cells can only flag a
          missing Labeeb value for a merchant already in the list; nothing can
          flag a merchant who never enters it. */}
      <ProductScope owns={["mcp"]}>
        the list is MCP&rsquo;s verification ledger — a merchant who uses only Labeeb does not
        appear here at all
      </ProductScope>

      {/* The list only holds merchants seen in the last year, so a dormant one
          is absent and otherwise unreachable — yet it can still be blocked.
          Without this field the 404 state would be decorative. */}
      <Panel className="p-4">
        <p className="mb-2 text-xs text-slate-500 dark:text-slate-400">
          Open any merchant by reference, including one not listed below:
        </p>
        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            const v = lookup.trim();
            if (v) navigate(`/tenants/${encodeURIComponent(v)}`);
          }}
        >
          {/* The width goes on a WRAPPER. F7's Input renders inside its own
              `w-full` span, so `className="w-56"` on the Input was overridden
              and the field filled the panel — pushing Open onto its own line
              and making a one-field lookup two rows tall. */}
          <div className="w-64">
            <Input
              value={lookup}
              onChange={(e) => setLookup(e.target.value)}
              placeholder="Business reference, e.g. 507077"
              aria-label="Business reference"
            />
          </div>
          <Button type="submit" disabled={!lookup.trim()}>
            Open
          </Button>
        </form>
      </Panel>

      {state.kind === "loading" && <Loading what="merchants" />}
      {state.kind === "signedout" && <SignedOut />}
      {state.kind === "denied" && <Denied message={state.error.message} />}
      {state.kind === "unavailable" && (
        <CouldNotLook what="the merchant list" message={state.error.message} onRetry={reload} />
      )}
      {state.kind === "notfound" && <LoadFailed what="merchants" message="Route not found." />}
      {state.kind === "invalid" && (
        <LoadFailed what="merchants" message={state.error.message} />
      )}
      {state.kind === "error" && <LoadFailed what="merchants" message={state.error.message} />}

      {state.kind === "ready" &&
        (() => {
          const all: McpTenantSummary[] = tenantsOf(state.data);
          const everywhere = state.data.users_blocked_across_all_merchants ?? [];
          // Named tiers are already narrowed by the server. TIER_UNSET is the
          // one slice ?tier= cannot express — filter the unfiltered list here.
          const byTier =
            tier === TIER_UNSET ? all.filter((r) => r.tier === null) : all;
          const rows = q
            ? byTier.filter((r) =>
                `${r.business_reference} ${r.business_name} ${r.email}`
                  .toLowerCase()
                  .includes(q.toLowerCase()),
              )
            : byTier;

          return (
            <>
              <FilterBar>
                <SearchField
                  value={q}
                  onChange={setQ}
                  placeholder="Filter by reference, name or email…"
                  label="Filter merchants"
                />
                <SelectField value={tier} onChange={setTier} label="Filter by tier" width={150}>
                  <option value="">Every tier</option>
                  {(state.data.known_tiers ?? []).map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                  <option value={TIER_UNSET}>not set</option>
                </SelectField>
                <Count shown={rows.length} total={all.length} />
              </FilterBar>

              {rows.length === 0 && (
                <Empty>
                  {q
                    ? "Nothing matches that filter."
                    : tier
                      ? "No merchants on that tier."
                      : "No merchants have connected yet."}
                </Empty>
              )}

              {/* NOT dense. Every row here carries two lines in three of its
                  cells plus an activity cell — the one table in the design
                  left at full height, because dense squeezes the second line
                  into the first and the reference under the name stops being
                  readable. */}
              {rows.length > 0 && (
              <Table
                dense={false}
                head={
                  <tr>
                    <Th>Reference</Th>
                    <Th>Name</Th>
                    <Th>Email</Th>
                    <Th>Tier</Th>
                    {/* The design's per-product columns, in its order.

                        "Stored as" is the one that earns its width: a merchant
                        holds a tier in EACH product, and the pair can disagree
                        — the design flags that as a `tier split`. One "Tier"
                        column cannot show a split, and a split is the failure
                        this table exists to catch.

                        Agents and Activity are Labeeb's and a usage-history
                        route neither product serves; their cells say so rather
                        than sitting blank. */}
                    <Th>Stored as</Th>
                    <Th>Agents</Th>
                    <Th>Status</Th>
                    <Th>Activity · 30d</Th>
                  </tr>
                }
                    >
                {rows.map((m) => (
                  // Ops know the numeric reference; the UUID is internal and
                  // never surfaced — it is not even in the type.
                  <tr key={m.business_reference} className="hover:bg-slate-950/[2.5%] dark:hover:bg-white/5">
                    <Td>
                      <Link
                        to={`/tenants/${encodeURIComponent(m.business_reference)}`}
                        className="ref"
                      >
                        {m.business_reference}
                      </Link>
                    </Td>
                    <Td>{m.business_name}</Td>
                    <Td>
                      <span className="text-slate-500 dark:text-slate-400">{m.email}</span>
                    </Td>
                    <Td>
                      {/* null is NOT "free". The server resolves an absent row
                          as free, but nothing has been stored — showing "free"
                          would claim a decision nobody made. */}
                      {m.tier ? (
                        <span className="ref">{m.tier}</span>
                      ) : (
                        <span className="text-slate-400 dark:text-slate-500" title="No entitlements row stored. The server treats this merchant as free until one is written.">
                          not set
                        </span>
                      )}
                    </Td>
                    {/* What each product actually stores. `tier` above is
                        MCP's, so this is not a duplicate of it — it is the
                        only place the reader can see that Labeeb's half is
                        missing rather than empty. */}
                    <Td>
                      <StoredAs stored={{ mcp: m.tier }} />
                    </Td>
                    <Td>
                      <NotWired id="labeeb" what="Effective agents for this merchant" />
                    </Td>
                    <Td>
                      {/* PER PRODUCT, and named as such. The old badge said
                          "Active", which claimed more than the console knows:
                          `blocked` is MCP's blocklist and nothing here can see
                          whether Labeeb has suspended the same merchant. A
                          merchant suspended in Labeeb and clear in MCP would
                          have read as simply Active. */}
                      <div className="flex flex-wrap items-center gap-1">
                        {m.blocked ? (
                          <Badge tone="red">MCP blocked</Badge>
                        ) : (
                          <Badge tone="green">MCP active</Badge>
                        )}
                        <NotWired id="labeeb" what="Whether this merchant is suspended in Labeeb" />
                      </div>
                      {blockedUserCount(m) > 0 && (
                        <span className="mt-0.5 block text-xs text-slate-500 dark:text-slate-400">
                          {blockedUserCount(m)} user
                          {blockedUserCount(m) === 1 ? "" : "s"} blocked
                        </span>
                      )}
                    </Td>
                    <Td>
                      {/* Shape only, and unreadable by construction. aria-hidden
                          so a screen reader reads the row as ending at Status
                          rather than meeting a cell of decoration. */}
                      <span
                        aria-hidden="true"
                        title="30-day activity — needs a usage history route on either product"
                        className="inline-block h-4 w-20 rounded bg-slate-950/10 blur-[3px] select-none dark:bg-white/10"
                      />
                    </Td>
                  </tr>
                ))}
              </Table>
              )}

              {/* NOT a property of any merchant above — these are ("*", email)
                  rows, blocked everywhere. Reported separately for that reason.

                  No `mt-4`: the column this sits in is the Page's own
                  `gap-6`, and a margin on top of it made the gap under the
                  table half again as large as every other gap on the page. */}
              {everywhere.length > 0 && (
                <Notice tone="amber" title="Blocked across all merchants">
                  <p className="text-xs">
                    These are not attached to any merchant in the list — they apply
                    everywhere.
                  </p>
                  <ul className="mt-1 space-y-0.5">
                    {everywhere.map((e) => (
                      <li key={e} className="ref text-xs">
                        {e}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-2 text-xs">
                    <Link to="/tenants/access" className="underline">
                      Manage blocks
                    </Link>
                  </p>
                </Notice>
              )}

              <p className="text-xs text-slate-500 dark:text-slate-400">
                {rows.length} shown
                {q && byTier.length !== rows.length ? ` of ${byTier.length}` : ""}.
                Merchants appear here from the verification ledger, whose entries expire
                after a year — a dormant merchant is absent but can still be opened by
                reference above.
              </p>
            </>
          );
        })()}
    </Page>
  );
}
